import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, ArrowLeft, MapPin, Copy, Share2, ExternalLink, Settings2, History } from "lucide-react";
import { toast } from "sonner";
import JobSettingsOverride from "@/components/JobSettingsOverride";
import RewriteHistory from "@/components/RewriteHistory";

const STAGES = [
  { key: "INVITED", label: "Invited", color: "bg-neutral-200" },
  { key: "IN_PROGRESS", label: "In Progress", color: "bg-blue-200" },
  { key: "SUBMITTED", label: "Submitted", color: "bg-amber-200" },
  { key: "EVALUATED", label: "Evaluated", color: "bg-emerald-200" },
  { key: "OFFERED", label: "Offered", color: "bg-pink-200" },
  { key: "HIRED", label: "Hired", color: "bg-teal-200" },
  { key: "REJECTED", label: "Rejected", color: "bg-red-200" },
];

export default function JobDetail() {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [cands, setCands] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [dragging, setDragging] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [boards, setBoards] = useState({ linkedin: false, indeed: false, seek: false });
  const [publishing, setPublishing] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const bulkMove = async (stage) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    try {
      await api.patch(`/recruit/candidates-bulk/stage`, { candidate_ids: ids, stage });
      toast.success(`Moved ${ids.length} to ${stage}`);
      clearSelection();
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Move failed"); }
  };

  const bulkShare = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    try {
      const { data } = await api.post(`/recruit/candidates-bulk/share`, { candidate_ids: ids });
      const urls = data.candidates.map(c => `${c.name}: ${window.location.origin}/interview/${c.invite_id}`).join("\n");
      await navigator.clipboard.writeText(urls);
      toast.success(`Copied ${data.candidates.length} interview links`);
    } catch (e) { toast.error("Failed"); }
  };

  const load = async () => {
    const [j, c] = await Promise.all([
      api.get(`/recruit/jobs/${jobId}`),
      api.get(`/recruit/candidates`, { params: { job_id: jobId } }),
    ]);
    setJob(j.data); setCands(c.data);
  };
  useEffect(() => { load(); }, [jobId]);

  const addCandidate = async () => {
    try {
      await api.post("/recruit/candidates", { ...form, job_id: jobId });
      setForm({ name: "", email: "" }); setOpen(false);
      toast.success("Candidate invited");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const onDrop = async (stage) => {
    if (!dragging || dragging.stage === stage) return;
    const orig = [...cands];
    setCands(cands.map(c => c.candidate_id === dragging.candidate_id ? { ...c, stage } : c));
    try {
      await api.patch(`/recruit/candidates/${dragging.candidate_id}/stage`, { stage });
      toast.success(`Moved to ${stage}`);
    } catch (e) { setCands(orig); toast.error("Move failed"); }
    setDragging(null);
  };

  const copyInvite = (invite) => {
    const url = `${window.location.origin}/interview/${invite}`;
    navigator.clipboard.writeText(url);
    toast.success("Interview link copied");
  };

  const publish = async () => {
    const selected = Object.entries(boards).filter(([_, v]) => v).map(([k]) => k);
    if (selected.length === 0) { toast.error("Pick at least one board"); return; }
    setPublishing(true);
    try {
      await api.post(`/recruit/jobs/${jobId}/publish`, { boards: selected });
      toast.success(`Published to ${selected.length} board${selected.length > 1 ? "s" : ""}`);
      setPublishOpen(false);
      setBoards({ linkedin: false, indeed: false, seek: false });
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Publish failed"); }
    finally { setPublishing(false); }
  };

  const unpublish = async (board) => {
    try {
      await api.post(`/recruit/jobs/${jobId}/unpublish`, { boards: [board] });
      toast.success("Unpublished");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  if (!job) return <div>Loading…</div>;

  return (
    <div className="space-y-6" data-testid="job-detail-page">
      <div>
        <Link to="/app/recruit/jobs" className="text-sm text-neutral-500 hover:text-pink flex items-center gap-1" data-testid="back-to-jobs">
          <ArrowLeft className="w-3.5 h-3.5" /> All jobs
        </Link>
        <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold">{job.title}</h1>
            <div className="mt-2 flex items-center gap-3 text-neutral-500">
              <Badge variant="outline">{job.department}</Badge>
              <span className="flex items-center gap-1 text-sm"><MapPin className="w-3.5 h-3.5" />{job.location}</span>
              <Badge className="bg-teal text-white border-0">{job.status}</Badge>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setSettingsOpen(true)} data-testid="job-settings-btn">
              <Settings2 className="w-4 h-4 mr-1" /> Job settings
            </Button>
            <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="publish-boards-btn"><Share2 className="w-4 h-4 mr-1" /> Publish to boards</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Publish "{job.title}"</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                  <p className="text-xs text-neutral-500">Distribute this role to public job boards in one click.
                    <span className="ml-1 inline-flex items-center rounded-full px-2 py-0.5 bg-amber-100 text-amber-800 font-semibold text-[10px] uppercase tracking-wider">MOCKED</span>
                  </p>
                  {[
                    { k: "linkedin", label: "LinkedIn Jobs" },
                    { k: "indeed", label: "Indeed" },
                    { k: "seek", label: "Seek" },
                  ].map((b) => (
                    <label key={b.k} className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-neutral-50">
                      <Checkbox checked={boards[b.k]} onCheckedChange={(v) => setBoards({...boards, [b.k]: !!v})} data-testid={`board-${b.k}`} />
                      <div>
                        <div className="font-medium text-sm">{b.label}</div>
                        <div className="text-xs text-neutral-500">One-click distribution</div>
                      </div>
                    </label>
                  ))}
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setPublishOpen(false)}>Cancel</Button>
                  <Button className="btn-primary" onClick={publish} disabled={publishing} data-testid="confirm-publish-btn">{publishing ? "Publishing…" : "Publish"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary rounded-full" data-testid="invite-candidate-btn"><Plus className="w-4 h-4 mr-1" /> Invite candidate</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Invite a candidate</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                  <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1.5" data-testid="invite-name" /></div>
                  <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-1.5" data-testid="invite-email" /></div>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button className="btn-primary" onClick={addCandidate} data-testid="send-invite-btn">Send invitation</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        {/* Publications strip */}
        {job.publications && Object.keys(job.publications).length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="publications-strip">
            <span className="text-xs uppercase tracking-widest text-neutral-500 mr-1">Published to:</span>
            {Object.entries(job.publications).map(([board, info]) => (
              <div key={board} className="flex items-center gap-2 rounded-full bg-white border px-3 py-1 text-xs" data-testid={`pub-${board}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-teal" />
                <span className="capitalize font-medium">{board}</span>
                <a href={info.external_url} target="_blank" rel="noreferrer" className="text-neutral-400 hover:text-pink"><ExternalLink className="w-3 h-3" /></a>
                <span className="inline-flex items-center rounded-full px-1.5 bg-amber-100 text-amber-800 font-semibold text-[9px] uppercase tracking-wider">MOCKED</span>
                <button onClick={() => unpublish(board)} className="text-neutral-400 hover:text-red-500 ml-1">×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Description */}
      {job.description && (
        <Card className="border-0 shadow-sm" data-testid="job-description-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Job description</h3>
              <div className="flex items-center gap-2 flex-wrap">
                {job.requirements?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {job.requirements.slice(0, 6).map(r => <Badge key={r} variant="outline" className="text-xs">{r}</Badge>)}
                  </div>
                )}
                <Button variant="ghost" size="sm" onClick={() => setHistoryOpen(true)} data-testid="job-history-btn">
                  <History className="w-3.5 h-3.5 mr-1" /> History
                </Button>
              </div>
            </div>
            <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm text-neutral-700 leading-relaxed" data-testid="job-description-text">
              {job.description}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk action bar (floating) */}
      {selected.size > 0 && (
        <div className="sticky top-4 z-20 rounded-full bg-navy text-white px-5 py-3 flex flex-wrap items-center gap-3 shadow-lg" data-testid="bulk-action-bar">
          <span className="font-semibold text-sm"><span className="text-pink">{selected.size}</span> selected</span>
          <div className="h-4 w-px bg-white/20" />
          <select onChange={(e) => { if (e.target.value) { bulkMove(e.target.value); e.target.value = ""; } }}
            className="rounded-full bg-white/10 text-white text-xs px-3 py-1.5 outline-none" data-testid="bulk-move-select">
            <option value="">Move to…</option>
            {STAGES.map(s => <option key={s.key} value={s.key} className="text-black">{s.label}</option>)}
          </select>
          <Button size="sm" variant="ghost" onClick={() => bulkMove("REJECTED")} className="text-white hover:bg-white/10 rounded-full text-xs" data-testid="bulk-reject-btn">Reject</Button>
          <Button size="sm" variant="ghost" onClick={bulkShare} className="text-white hover:bg-white/10 rounded-full text-xs" data-testid="bulk-share-btn"><Share2 className="w-3 h-3 mr-1" /> Share links</Button>
          <button onClick={clearSelection} className="ml-auto text-white/60 hover:text-white text-xs" data-testid="bulk-clear-btn">Clear</button>
        </div>
      )}

      {/* Kanban */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 pb-6" data-testid="kanban-board">
        {STAGES.map((s) => {
          const items = cands.filter(c => c.stage === s.key);
          return (
            <div key={s.key} className="kanban-col p-3 min-h-[300px]"
              onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(s.key)}
              data-testid={`kanban-col-${s.key}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${s.color}`} />
                  <div className="text-xs font-semibold uppercase tracking-wider">{s.label}</div>
                </div>
                <Badge variant="outline" className="text-[10px]">{items.length}</Badge>
              </div>
              <div className="space-y-2">
                {items.map((c) => {
                  const isSel = selected.has(c.candidate_id);
                  return (
                  <div key={c.candidate_id} draggable onDragStart={() => setDragging(c)} onDragEnd={() => setDragging(null)}
                    className={`kanban-card p-3 ${isSel ? "ring-2 ring-pink" : ""}`} data-testid={`kanban-card-${c.candidate_id}`}>
                    <div className="flex items-start gap-2">
                      <Checkbox checked={isSel} onCheckedChange={() => toggleSelect(c.candidate_id)}
                        className="mt-0.5" data-testid={`kanban-checkbox-${c.candidate_id}`}
                        onClick={(e) => e.stopPropagation()} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <Link to={`/app/recruit/candidates/${c.candidate_id}`} className="min-w-0">
                            <div className="font-semibold text-sm truncate">{c.name}</div>
                            <div className="text-xs text-neutral-500 truncate">{c.email}</div>
                          </Link>
                          <button onClick={() => copyInvite(c.invite_id)} className="text-neutral-400 hover:text-pink" title="Copy invite link">
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="mt-2 flex items-center gap-1.5 text-[10px]">
                          <Badge variant="outline" className="text-[10px]">{c.assessment_type}</Badge>
                          {c.ai_score != null && (
                            <span className="text-pink font-bold font-mono">{c.ai_score}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <JobSettingsOverride jobId={jobId} open={settingsOpen} onOpenChange={setSettingsOpen} />
      <RewriteHistory jobId={jobId} open={historyOpen} onOpenChange={setHistoryOpen} onReverted={load} />
    </div>
  );
}
